import React, { useEffect, useState, useRef, lazy, createElement } from 'react';
import { useRevealAnimation } from '../utils/IntersectionObserverUtil';
export function Hero() {
  // Use centralized reveal animation
  useRevealAnimation();
  // Reference for the video container
  const videoRef = useRef<HTMLDivElement>(null);
  // State to prevent re-initialization after first load
  const [videoLoaded, setVideoLoaded] = useState(false);
  useEffect(() => {
    if (!videoRef.current) return;
    const options = {
      root: null,
      rootMargin: '200px',
      threshold: 0.1
    };
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && !videoLoaded) {
        // Create video element
        const video = document.createElement('video');
        video.src = 'https://cdn.shopify.com/videos/c/o/v/c239c42522814b32af3cf5206bdab235.mp4#t=5';
        video.className = 'absolute top-0 left-0 w-full h-full object-cover rounded-lg';
        video.controls = true;
        video.preload = 'metadata';
        video.playsInline = true;
        if (videoRef.current) {
          // Append video
          videoRef.current.appendChild(video);
          // Create overlay play button
          const playButton = document.createElement('div');
          playButton.className = 'absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 cursor-pointer rounded-lg z-10';
          const playIcon = document.createElement('div');
          playIcon.className = 'bg-white bg-opacity-80 text-teal-600 rounded-full p-3 shadow-lg';
          playIcon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-play"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>`;
          playButton.appendChild(playIcon);
          videoRef.current.appendChild(playButton);
          // Add click handler to play video from beginning
          playButton.addEventListener('click', () => {
            if (!videoRef.current) return;
            playButton.style.display = 'none';
            video.currentTime = 0;
            video.play();
          });
          // Set flag to prevent re-loading
          setVideoLoaded(true);
          // Disconnect observer after loading
          observer.disconnect();
        }
      }
    };
    const observer = new IntersectionObserver(handleIntersection, options);
    observer.observe(videoRef.current);
    return () => {
      observer.disconnect();
    };
  }, [videoLoaded]);
  return <div className="w-full bg-gradient-to-r from-blue-50 to-teal-50 py-8 md:py-12 lg:py-16 xl:py-20 gradient-animation" data-id="element-387">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-8 lg:px-8" data-id="element-388">
        <div className="flex flex-col items-center gap-4 md:gap-5 lg:gap-6 xl:gap-8" data-id="element-389">
          {/* Headlines Section - optimized for mobile first */}
          <div className="text-left w-full max-w-4xl space-y-2 sm:space-y-3 md:space-y-3.5 lg:space-y-4" data-id="element-390">
            <h2 className="text-3xl sm:text-4xl md:text-5xl md:font-extrabold lg:text-5xl xl:text-6xl font-extrabold text-gray-900 leading-tight" data-id="element-391">
              If You Use Eye Drops,
            </h2>
            <h2 className="text-3xl sm:text-4xl md:text-5xl md:font-extrabold lg:text-5xl xl:text-6xl font-extrabold text-teal-600 leading-tight mb-4 sm:mb-5 md:mb-4 lg:mb-6" data-id="element-392">
              You Need to Read This.
            </h2>
            {/* Badge - optimized for mobile */}
            <div className="inline-block" data-id="element-393">
              <div className="bg-gradient-to-r from-teal-500 to-blue-500 text-white text-sm sm:text-sm md:text-base lg:text-base font-semibold uppercase tracking-wide py-2.5 px-4 md:py-2.5 md:px-5 lg:py-3 lg:px-6 rounded inline-block leading-relaxed max-w-full md:max-w-2xl lg:max-w-3xl" data-id="element-394">
                Exposing $4.8 Billion Lies & the 15-Minute Daily Care
                They Spent Millions to Bury
              </div>
            </div>
          </div>
          {/* Image Section - optimized for tablet and desktop */}
          <div className="w-full max-w-2xl md:max-w-2xl lg:max-w-3xl mx-auto reveal-on-scroll mt-3 md:mt-4 lg:mt-6" data-id="element-395">
            <div className="rounded-lg overflow-hidden border border-gray-200 shadow-lg bg-white" data-id="element-396">
              <img src="https://cdn.shopify.com/s/files/1/0679/7135/0570/files/unnamed_18.jpg?v=17611193181" alt="Comparison showing eye drops with a red cross (ineffective) versus heat therapy with a green check (effective solution)" className="w-full h-auto object-contain" width="800" height="450" fetchpriority="high" decoding="async" onError={e => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = 'https://placehold.co/600x400/e2e8f0/64748b?text=Eye+Treatment+Comparison';
            }} data-id="element-397" />
            </div>
          </div>
          {/* Author Section - optimized for tablet */}
          <div className="text-center max-w-4xl w-full" data-id="element-398">
            <div className="flex items-center justify-center mb-5 md:mb-6 lg:mb-8 reveal-on-scroll" data-id="element-399">
              <div className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full bg-gray-200 mr-3 md:mr-4 overflow-hidden hover-zoom flex-shrink-0" data-id="element-400">
                <img src="https://cdn.shopify.com/s/files/1/0679/7135/0570/files/Gemini_Generated_Image_1i68bd1i68bd1i68.png?v=1761116494" alt="Dr. Jennifer Martinez" className="w-full h-full object-cover" width="56" height="56" loading="lazy" data-id="element-401" />
              </div>
              <div className="text-left" data-id="element-402">
                <p className="font-medium text-gray-900 text-base md:text-lg lg:text-xl" data-id="element-403">
                  Dr. Linda Matthews
                </p>
                <p className="text-sm md:text-base lg:text-lg text-gray-600" data-id="element-404">
                  Board Certified Dry-Eye Expert
                </p>
                <p className="text-sm md:text-base lg:text-lg text-gray-600" data-id="element-405">
                  Updated April 7th 2025
                </p>
              </div>
            </div>
            {/* Content - optimized for tablet */}
            <div className="text-gray-800 leading-relaxed space-y-4 md:space-y-4 lg:space-y-5 text-base md:text-lg lg:text-xl" data-id="element-406">
              <p className="font-semibold text-teal-600" data-id="element-407">
                Do you remember the last time you cried and it felt... good?
              </p>
              <p data-id="element-408">
                Not the burning, stinging mess your tears have become. But real
                tears, the kind that used to wash everything clean.
              </p>
              <p data-id="element-409">
                I'm talking about{' '}
                <span className="font-semibold text-teal-600" data-id="element-410">1994</span>.
                Watching <em data-id="element-411">Forrest Gump</em>. Letting those tears roll freely
                as Jenny finally came home. Your eyes felt refreshed afterward.
                Lighter. Like the world looked brighter somehow.
              </p>
              <p data-id="element-412">
                Or <span className="font-semibold text-teal-600" data-id="element-413">2001</span>.
                Your daughter's wedding. Happy tears that didn't leave your eyes
                feeling like sandpaper for a week.
              </p>
              <p data-id="element-414">
                <span className="font-bold" data-id="element-415">Now?</span>
                <br data-id="element-416" />
                Now you can't even watch a Hallmark commercial without paying
                for it. Your eyes water constantly but it's the wrong kind of
                tears. The kind that burn. Sting. Make you look like you've been
                up for three days.
              </p>
              <p data-id="element-417">
                One of my patients,{' '}
                <span className="font-semibold" data-id="element-418">Margaret</span>, brought in her
                wedding album last week. Pointed to a photo from 1978.{' '}
                <em data-id="element-419">"Look at my eyes,"</em> she said.
                <em data-id="element-420">
                  "They were so white. So clear. When did I start looking so...
                  tired?"
                </em>
              </p>
              <p className="font-semibold text-teal-600" data-id="element-421">
                I'll tell you exactly when.
                <br data-id="element-422" />
                It was the day your tears stopped working.
              </p>
              <p data-id="element-423">
                See, when we were younger, every tear was perfect. Three layers
                working in harmony: water, protein, and oil. Like a recipe your
                grandmother never wrote down but somehow always got right.
              </p>
              <p data-id="element-424">
                But somewhere between then and now, between those birthday
                candles you stopped counting and the readers you swear you don't
                need... something broke.
              </p>
              <p data-id="element-425">
                The oil glands in your eyelids started shutting down. One by
                one. Like lights going out in the neighborhood you grew up in.
              </p>
              <p className="font-semibold text-teal-600" data-id="element-426">
                And once you see why that happens, you'll never think about 'dry
                eyes' the same way again.
              </p>
              {/* Video section */}
              <div className="mb-8" data-id="element-427">
                <div className="max-w-3xl mx-auto" data-id="element-428">
                  <div className="bg-gray-100 p-3 rounded-lg shadow-sm mb-3" data-id="element-429">
                    <p className="text-center text-gray-700 font-medium" data-id="element-430">
                      <span className="text-teal-600" data-id="element-431">▶️</span> Watch this short
                      video to understand what's really happening with your eyes
                    </p>
                  </div>
                  <div className="rounded-lg overflow-hidden shadow-lg" data-id="element-432">
                    {/* Container for intersection observer */}
                    <div className="relative w-full bg-black" style={{
                    paddingBottom: '56.25%',
                    position: 'relative',
                    height: 0
                  }} ref={videoRef} data-id="element-433">
                      {/* Video will be loaded here via intersection observer */}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 text-center mt-2 italic" data-id="element-434"></p>
                </div>
              </div>
              <p className="text-gray-800 leading-relaxed mb-4" data-id="element-435">
                Now that you've seen what's actually going on, the question
                becomes...
              </p>
              <p className="text-gray-800 leading-relaxed" data-id="element-436">
                <strong data-id="element-437">
                  How do you safely restore and melt those blocked oils
                </strong>
                <span className="text-teal-600 font-semibold" data-id="element-438">
                  ...without resorting to painful $1,500 temporary treatments?
                </span>
                <br data-id="element-439" />
                Or getting trapped in another
                <span className="text-teal-600 font-semibold" data-id="element-440">
                  {' '}
                  eye-drop subscription{' '}
                </span>
                that only makes things worse?
              </p>
              <p className="font-semibold text-teal-600" data-id="element-441">
                But here's what really breaks my heart...
              </p>
              <p data-id="element-442">
                One patient said she used to start every morning with a book and
                tea by the window. "Now I just sit there," she said. "The light
                hurts too much to concentrate.
              </p>
              <p data-id="element-443">
                Another patient hasn't watched a full movie with her husband in
                five years. Not because she doesn't want to. But because her
                eyes force her to leave halfway through.{' '}
                <strong data-id="element-444">Every. Single. Time.</strong>
              </p>
              <p data-id="element-445">
                These aren't just <em data-id="element-446">"symptoms."</em>
                These are{' '}
                <span className="font-semibold text-teal-600" data-id="element-447">
                  stolen moments. Lost connections.
                </span>
                The gradual theft of everything that makes life rich.
              </p>
              <p className="font-bold" data-id="element-448">
                And every general doctor just keeps saying, "Try these drops."
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}